import { motion } from 'framer-motion';
import { type MouseEvent,useEffect, useState } from 'react';
import { FiMenu, FiMoon, FiSun,FiX } from 'react-icons/fi';
import { useLocation,useNavigate } from 'react-router';

import { useTheme } from '../../context/ThemeContext';
import OptimizedImage from '../OptimizedImage';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleNavigation = (event: MouseEvent<HTMLAnchorElement>, targetId: string) => {
    event.preventDefault();

    if (isOpen) {
      setIsOpen(false);
    }

    const scrollToSection = () => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    if (location.pathname !== '/') {
      navigate('/', { state: { scrollToId: targetId } });
    } else {
      scrollToSection();
    }
  };

  return (
    <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-card dark:bg-panel shadow-md py-2' : 'bg-transparent py-2'
        }`}
      >
        <div className="container-custom flex justify-between items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center"
        >
          <a href="/" className="flex items-center h-10 w-auto">
            <OptimizedImage
              src={isDarkMode ? "/logo_light.png" : "/logo_dark.png"}
              alt="Konnect Better Logo"
              objectFit="contain"
              priority={true}
              className="max-w-[150px] md:max-w-none"
            />
          </a>
        </motion.div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <a href="#about" className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors" onClick={(event) => handleNavigation(event, 'about')}>
            About
          </a>
          <a href="#app" className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors" onClick={(event) => handleNavigation(event, 'app')}>
            App
          </a>
          <a href="#products" className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors" onClick={(event) => handleNavigation(event, 'products')}>
            Products
          </a>
          <a href="#team" className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors" onClick={(event) => handleNavigation(event, 'team')}>
            Team
          </a>
          <a href="#faq" className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors" onClick={(event) => handleNavigation(event, 'faq')}>
            FAQ
          </a>
          <a href="/wisdom" className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors">
            Wisdom
          </a>
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full transition-colors hover:bg-panel/60"
            aria-label="Toggle dark mode"
            aria-pressed={isDarkMode}
          >
            {isDarkMode ? <FiSun className="text-theme-primary" /> : <FiMoon className="text-theme-secondary" />}
          </button>
          <a href="/joincommunity" className="btn-secondary">
            Contact
          </a>
        </nav>

        {/* Mobile Navigation Toggle */}
        <div className="flex items-center md:hidden">
          <button
            onClick={toggleTheme}
            className="p-2 mr-2 rounded-full transition-colors hover:bg-panel/60"
            aria-label="Toggle dark mode"
            aria-pressed={isDarkMode}
          >
            {isDarkMode ? <FiSun className="text-theme-primary" /> : <FiMoon className="text-theme-secondary" />}
          </button>
          <button
            onClick={toggleMenu}
            className="p-2 rounded-full transition-colors hover:bg-panel/60"
            aria-label="Toggle menu"
          >
            {isOpen ?
              <FiX className="h-6 w-6 text-theme-primary" /> :
              <FiMenu className="h-6 w-6 text-theme-primary" />
            }
          </button>
        </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-card dark:bg-panel shadow-lg"
          >
            <div className="container-custom py-4 flex flex-col space-y-4">
            <a
              href="#about"
              className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors py-2"
              onClick={(event) => handleNavigation(event, 'about')}
            >
              About
            </a>
            <a
              href="#app"
              className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors py-2"
              onClick={(event) => handleNavigation(event, 'app')}
            >
              App
            </a>
            <a
              href="#products"
              className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors py-2"
              onClick={(event) => handleNavigation(event, 'products')}
            >
              Products
            </a>
            <a
              href="#team"
              className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors py-2"
              onClick={(event) => handleNavigation(event, 'team')}
            >
              Team
            </a>
            <a
              href="#faq"
              className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors py-2"
              onClick={(event) => handleNavigation(event, 'faq')}
            >
              FAQ
            </a>
            <a
              href="/wisdom"
              className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors py-2"
              onClick={() => setIsOpen(false)}
            >
              Wisdom
            </a>
            <a
              href="/joincommunity"
              className="btn-secondary w-full text-center"
              onClick={() => setIsOpen(false)}
            >
              Contact
            </a>
            </div>
          </motion.div>
        )}
      </header>
  );
};

export default Header;
